import { createContext, ReactNode, useContext, useMemo } from "react";
import type { DescendantClassroom } from "../../queries/general";

/**
 * Espone i dati di `descendants-classrooms` (una sola chiamata per pagina di dettaglio)
 * alle card figlie, che altrimenti dovrebbero interrogare un endpoint per card.
 *
 * Oltre ai dati espone lo stato della chiamata, perché un record assente ha tre significati
 * diversi che le card devono poter distinguere: dato non ancora arrivato, dato che non
 * arriverà (errore o chiamata non eseguita), utente non iscritto.
 */
interface DescendantsClassroomsContextType {
  getDescendantClassroom: (loId?: number) => DescendantClassroom | undefined;
  /** Chiamata in corso: lo stato di iscrizione non è ancora noto. */
  isLoading: boolean;
  /** Chiamata fallita o non eseguita: il dato di iscrizione non arriverà. */
  isUnavailable: boolean;
  /**
   * Corso/percorso ad auto-iscrizione con approvazione: true quando il responsabile ha concesso
   * l'accesso (o l'iscrizione non richiede approvazione). Finché è false nessun figlio è
   * fruibile e le relative CTA restano disabilitate.
   * Viaggia nel contesto e non come prop perché le card annidate stanno sotto
   * PathCourseAccordion → AccordionActivity → ActivityDotNavigation.
   */
  approvalGranted: boolean;
  /**
   * `enrollType` del padre (percorso/corso) o, per le attività standalone, dell'oggetto stesso.
   * Usato dalle aule figlie per sapere se l'iscrizione a una loro edizione passa sempre da una
   * richiesta di approvazione ("REQUESTED_AUTO_ENROLL"), indipendentemente da `approvalGranted`.
   */
  enrollType?: string | null;
}

const DescendantsClassroomsContext = createContext<DescendantsClassroomsContextType | null>(null);

export const DescendantsClassroomsProvider = ({
  children,
  descendants = [],
  isLoading = false,
  isUnavailable = false,
  approvalGranted = true,
  enrollType = null
}: {
  children: ReactNode
  descendants?: DescendantClassroom[]
  isLoading?: boolean
  isUnavailable?: boolean
  approvalGranted?: boolean
  enrollType?: string | null
}) => {
  const value = useMemo(() => {
    const byLoId = new Map<number, DescendantClassroom>();
    descendants.forEach((item) => byLoId.set(item.idLo, item));
    return {
      approvalGranted,
      enrollType,
      getDescendantClassroom: (loId?: number) => (loId == null ? undefined : byLoId.get(loId)),
      isLoading,
      isUnavailable
    };
  }, [descendants, isLoading, isUnavailable, approvalGranted, enrollType]);

  return (
    <DescendantsClassroomsContext.Provider value={ value }>
      { children }
    </DescendantsClassroomsContext.Provider>
  );
};

/**
 * Fuori dal provider il dato non è semplicemente vuoto: non è disponibile.
 * Le card lo trattano come tale e ricadono sul criterio basato sulle edizioni.
 */
export const useDescendantClassrooms = (): DescendantsClassroomsContextType => {
  const context = useContext(DescendantsClassroomsContext);
  return context ?? {
    approvalGranted: true,
    enrollType: null,
    getDescendantClassroom: () => undefined,
    isLoading: false,
    isUnavailable: true
  };
};
